/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Question from "../../components/Question";
import MoveButton from "../../components/MoveButton";
import { continueButtonSx, inputLabelSx, textAreaSx } from "../../lib/uiformatting";
import { Box, TextField, FormControl, Collapse, useMediaQuery, useTheme } from "@mui/material";
import { setHowWellWorks, setSideEffects } from "../../redux/userSlice";
import ContinueButton from "../../components/ContinueButton";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

const OPTIONS = {
    VERY_WELL: "Very well",
    SOMEWHAT: "Somewhat",
    WITH_SIDE_EFFECTS: "It worked with side effects",
};

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

export default function HowWellWorks() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { howWellWorks, sideEffects, isIsotretinoin } = useSelector((state) => state.user);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.down("sm"));

    const options = useMemo(() => {
        if (isIsotretinoin) {
            const { WITH_SIDE_EFFECTS, ...rest } = OPTIONS;
            return rest;
        }
        return OPTIONS;
    }, [isIsotretinoin]);

    const [localHowWellWorks, setLocalHowWellWorks] = useState(howWellWorks);
    const [localSideEffects, setLocalSideEffects] = useState(sideEffects?.description ?? "");
    const [error, setError] = useState(false);

    const debouncedSideEffects = useDebounce(localSideEffects, 300);

    useEffect(() => {
        if (isIsotretinoin && localHowWellWorks === OPTIONS.WITH_SIDE_EFFECTS) {
            setLocalHowWellWorks("");
            setLocalSideEffects("");
            setError(false);
            dispatch(setHowWellWorks(""));
            dispatch(setSideEffects({ has: "No", description: "" }));
        }
    }, [isIsotretinoin, dispatch]);

    useEffect(() => {
        if (localHowWellWorks === OPTIONS.WITH_SIDE_EFFECTS && !isIsotretinoin) {
            dispatch(setSideEffects({ has: "Yes", description: debouncedSideEffects }));
        }
    }, [dispatch, localHowWellWorks, debouncedSideEffects, isIsotretinoin]);

    const handleOptionChange = useCallback(
        (value) => {
            setLocalHowWellWorks(value);
            dispatch(setHowWellWorks(value));

            const nextRoute = isIsotretinoin ? "/medical-history-updates" : "/follow-up-care";

            if (value === OPTIONS.VERY_WELL || value === OPTIONS.SOMEWHAT) {
            setLocalSideEffects("");
            dispatch(setSideEffects({ has: "No", description: "" }));
            setError(false);
            setPageAndDeferNavigate(dispatch, nextRoute, navigate);
            } else {
            setError(false);
            }
        },[dispatch, navigate, isIsotretinoin]);

    const handleSideEffectsChange = useCallback((e) => {
        const value = e.target.value;
        setLocalSideEffects(value);
        setError(value === "");
    }, []);

    const showSideEffects =
        !isIsotretinoin && localHowWellWorks === OPTIONS.WITH_SIDE_EFFECTS;

    const isNextDisabled = useMemo(
        () => !localHowWellWorks || (showSideEffects && !localSideEffects),
        [localHowWellWorks, showSideEffects, localSideEffects]
    );

    const handleNext = useCallback(() => {
        dispatch(
            setSideEffects({
                has: showSideEffects ? "Yes" : "No",
                description: showSideEffects ? localSideEffects : "",
            })
        );
        if (isIsotretinoin) {
            setPageAndDeferNavigate(dispatch, "/medical-history-updates", navigate);
        } else {
            setPageAndDeferNavigate(dispatch, "/follow-up-care", navigate);
        }
        
    }, [dispatch, navigate, showSideEffects, localSideEffects]);

    const optionButtons = useMemo(
        () =>
            Object.entries(options).map(([key, value]) => (
                <MoveButton
                    key={key}
                    onClick={() => handleOptionChange(value)}
                    title={value}
                    selected={localHowWellWorks === value}
                />
            )),
        [options, handleOptionChange, localHowWellWorks]
    );

    const handleBack = () => {
        setPageAndDeferNavigate(dispatch, "/currently-prescribed", navigate);
    };

    return (
        <Question
            onBack={handleBack}
            returning={true}
            title="How well does it work for you?"
            subtitle="Select One"
            input={
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: isDesktop ? "flex-start" : "center",
                        width: "100%",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            width: "100%",
                            maxWidth: "500px",
                        }}
                    >
                        {optionButtons}
                    </Box>

                    <Collapse in={showSideEffects} sx={{ width: "100%", maxWidth: "500px", mt: 2 }}>
                        <FormControl sx={{ width: "100%", mt: "1rem" }} required>
                            <TextField
                                aria-labelledby="side-effects-label"
                                label="What are they?"
                                variant="outlined"
                                color="secondary"
                                sx={{
                                    ...textAreaSx,
                                    width: "100%",
                                    "& .MuiInputLabel-root": {
                                        backgroundColor: theme.palette.background.paper,
                                        padding: "0 32px",
                                        transition: "all 0.2s ease-in-out",
                                    },
                                }}
                                InputLabelProps={{
                                    sx: {
                                        ...inputLabelSx,
                                        "& .MuiInputLabel-shrink": {
                                            backgroundColor: theme.palette.background.paper,
                                        },
                                    },
                                }}
                                autoComplete="off"
                                multiline
                                rows={2}
                                value={localSideEffects}
                                onChange={handleSideEffectsChange}
                                error={error}
                                helperText={error ? "Please enter your side effects." : ""}
                            />
                        </FormControl>
                    </Collapse>
                </Box>
            }
            footer={
                !isIsotretinoin && (
                    <Box sx={{ ...continueButtonSx }}>
                        <ContinueButton
                            onClick={handleNext}
                            title="Continue"
                            disabled={isNextDisabled}
                            selected={!isNextDisabled}
                        />
                    </Box>
                )
            }
        />
    );
}
